// DataCollectionForm.js
import React from "react";
import {
  Box,
  Typography,
  TextField,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  InputLabel,
  Paper,
  Select,
  MenuItem,
  ListItemText,
  Chip,
  Slider,
  Button,
  CircularProgress,
  FormHelperText,
} from "@mui/material";
import ButtonCommon from "../../CommonComponent/ButtonCommon";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";

const PolicySettings = ({
  formData,
  setFormData,
  handleInputChange,
  handleBooleanChange,
  handleArrayInputChange,
  onBack,
  onSubmit,
  loading,
  errors,
  // isSubmitDisabled,
}) => {
  const personalDataTypes = [
    "First Name and Last Name",
    "Date of Birth",
    "National Identification Number",
    "Telephone Number and Email",
    "Address and City",
    "Payment Information",
    "Biometrics",
    "Device ID",
    "IP Address",
    "Browser Information and Language",
    "Operating System and Version",
    "Browser Fingerprint",
    "Real-time Location",
    "IP-based Location",
    "Browsing History",
    "Interaction Logs",
    "Health Data",
    "Purchase History",
    "Payment Method and History",
  ];

  const dataCollectionPurposes = [
    "Authentication and Security",
    "Compliance with Legal Obligations",
    "Customizing and Adapting User Experience",
    "Research and Development",
    "Content Delivery",
    "Customer Support",
    "Communication Efforts",
    "Fraud Prevention and Risk Management",
    "Analytics and Performance Tracking",
    "Payment Processing",
    "Marketing and Advertising",
    "User Engagement and Retention",
    "Displaying Videos",
    "Improving Public Health",
    "Processing Transactions",
    "User Feedback and Satisfaction",
    "Providing Access to Exclusive Content",
    "Tag Management",
  ];

  const dataProtectionOptions = {
    Encryption:
      "To protect data during transfer and at rest, we employ robust encryption technologies.",
    "Access Control":
      "Access to personal information is strictly limited to authorized personnel with a legitimate business need.",
    "Security Audits and Monitoring":
      "Regular security audits and continuous monitoring are conducted to identify and prevent unauthorized access.",
  };

  countries.registerLocale(enLocale);

  const countryList = Object.entries(
    countries.getNames("en", { select: "official" })
  )
    .map(([code, name]) => name)
    .sort((a, b) => a.localeCompare(b));

  return (
    <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
      <form onSubmit={onSubmit}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom color="textSecondary">
            Personal Data Types{" "}
            <Typography component="span" color="error">
              *
            </Typography>
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Select the types of personal data your company collects.
          </Typography>

          <FormControl
            fullWidth
            size="small"
            error={!!errors?.["config.personal_data.personal_data_types"]}
          >
            <Select
              labelId="personal-data-types-label"
              id="personal-data-types"
              multiple
              value={formData.config.personal_data.personal_data_types}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  config: {
                    ...formData.config,
                    personal_data: {
                      ...formData.config.personal_data,
                      personal_data_types: e.target.value,
                    },
                  },
                })
              }
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                  },
                },
              }}
            >
              {personalDataTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  <Checkbox
                    checked={formData.config.personal_data.personal_data_types.includes(
                      type
                    )}
                  />
                  <ListItemText primary={type} />
                </MenuItem>
              ))}
            </Select>
            {errors?.["config.personal_data.personal_data_types"] && (
              <FormHelperText variant="error">
                {errors["config.personal_data.personal_data_types"]}
              </FormHelperText>
            )}
          </FormControl>
        </Box>

        <Divider sx={{ mt: 3, mb: 3 }} />

        <Box sx={{ mt: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="textSecondary">
            Data Collection Purpose{" "}
            <Typography component="span" color="error">
              *
            </Typography>
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            What is the purpose for collecting personal data?
          </Typography>

          <FormControl
            fullWidth
            size="small"
            error={!!errors?.["config.data_collection_purpose.data_purposes"]}
          >
            <Select
              labelId="data-purpose-label"
              id="data-purpose-select"
              multiple
              value={formData.config.data_collection_purpose.data_purposes}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  config: {
                    ...prev.config,
                    data_collection_purpose: {
                      ...prev.config.data_collection_purpose,
                      data_purposes: e.target.value,
                    },
                  },
                }))
              }
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                  },
                },
              }}
            >
              {dataCollectionPurposes.map((purpose) => (
                <MenuItem key={purpose} value={purpose}>
                  <Checkbox
                    checked={formData.config.data_collection_purpose.data_purposes.includes(
                      purpose
                    )}
                  />
                  <ListItemText primary={purpose} />
                </MenuItem>
              ))}
            </Select>
            {errors?.["config.data_collection_purpose.data_purposes"] && (
              <FormHelperText>
                {errors["config.data_collection_purpose.data_purposes"]}
              </FormHelperText>
            )}
          </FormControl>
        </Box>

        <Divider sx={{ mt: 3, mb: 3 }} />

        <Box sx={{ mt: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="textSecondary">
            Data Storage Locations{" "}
            <Typography component="span" color="error">
              *
            </Typography>
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Where will the collected data be stored?
          </Typography>

          <FormControl
            fullWidth
            size="small"
            error={
              !!errors?.[
              "config.data_storage_and_protection.data_storage.locations"
              ]
            }
          >
            <Select
              labelId="country-select-label"
              id="country-select"
              multiple
              value={
                formData.config.data_storage_and_protection.data_storage
                  .locations
              }
              onChange={(e) =>
                setFormData({
                  ...formData,
                  config: {
                    ...formData.config,
                    data_storage_and_protection: {
                      ...formData.config.data_storage_and_protection,
                      data_storage: {
                        ...formData.config.data_storage_and_protection
                          .data_storage,
                        locations: e.target.value,
                      },
                    },
                  },
                })
              }
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
              MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
            >
              {countryList.map((country) => (
                <MenuItem key={country} value={country}>
                  <Checkbox
                    checked={formData.config.data_storage_and_protection.data_storage.locations.includes(
                      country
                    )}
                  />
                  <ListItemText primary={country} />
                </MenuItem>
              ))}
            </Select>
            {errors?.[
              "config.data_storage_and_protection.data_storage.locations"
            ] && (
                <FormHelperText>
                  {
                    errors[
                    "config.data_storage_and_protection.data_storage.locations"
                    ]
                  }
                </FormHelperText>
              )}
          </FormControl>

          <FormControl margin="normal">
            <FormLabel id="international-transfer-label">
              International Data Transfer
            </FormLabel>
            <RadioGroup
              row
              aria-labelledby="international-transfer-label"
              value={
                formData.config.data_storage_and_protection.data_storage
                  .international_data_transfer
              }
              onChange={(e) =>
                handleBooleanChange(
                  "config.data_storage_and_protection.data_storage.international_data_transfer",
                  e.target.value === "true"
                )
              }
            >
              <FormControlLabel
                value={true}
                control={<Radio id="international-yes" />}
                label="Yes"
              />
              <FormControlLabel
                value={false}
                control={<Radio id="international-no" />}
                label="No"
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <Divider sx={{ mt: 3, mb: 3 }} />

        <Box sx={{ mt: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="textSecondary">
            Data Protection{" "}
            <Typography component="span" color="error">
              *
            </Typography>
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Where will the collected data be stored?
          </Typography>

          <FormControl
            fullWidth
            size="small"
            error={
              !!errors?.[
              "config.data_storage_and_protection.data_protection_measures"
              ]
            }
          >
            <Select
              labelId="data-protection-label"
              id="data-protection-select"
              multiple
              value={Object.keys(
                formData.config.data_storage_and_protection
                  ?.data_protection_measures || {}
              )}
              onChange={(event) => {
                const selectedValues = event.target.value;
                setFormData((prev) => ({
                  ...prev,
                  config: {
                    ...prev.config,
                    data_storage_and_protection: {
                      ...prev.config.data_storage_and_protection,
                      data_protection_measures: selectedValues.reduce(
                        (acc, label) => {
                          acc[label] = dataProtectionOptions[label];
                          return acc;
                        },
                        {}
                      ),
                    },
                  },
                }));
              }}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                  },
                },
              }}
            >
              {Object.entries(dataProtectionOptions).map(
                ([label, description]) => (
                  <MenuItem key={label} value={label}>
                    <Checkbox
                      checked={Object.hasOwnProperty.call(
                        formData.config.data_storage_and_protection
                          ?.data_protection_measures || {},
                        label
                      )}
                    />
                    <ListItemText primary={label} secondary={description} />
                  </MenuItem>
                )
              )}
            </Select>

            {errors?.[
              "config.data_storage_and_protection.data_protection_measures"
            ] && (
                <FormHelperText>
                  {
                    errors[
                    "config.data_storage_and_protection.data_protection_measures"
                    ]
                  }
                </FormHelperText>
              )}
          </FormControl>
        </Box>

        <Divider sx={{ mt: 3, mb: 3 }} />

        <Box sx={{ mt: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="textSecondary">
            Data Sharing
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Do you share user data with third parties?
          </Typography>

          <FormControl>
            <RadioGroup
              row
              aria-labelledby="data-sharing-label"
              value={formData.config.data_sharing.data_sharing.yes}
              onChange={(e) =>
                handleBooleanChange(
                  "config.data_sharing.data_sharing.yes",
                  e.target.value === "true"
                )
              }
            >
              <FormControlLabel
                value={true}
                control={<Radio id="sharing-yes" />}
                label="Yes"
              />
              <FormControlLabel
                value={false}
                control={<Radio id="sharing-no" />}
                label="No"
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <Divider sx={{ mt: 3, mb: 3 }} />

        <Box sx={{ mt: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="textSecondary">
            Cookie Policy
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            If you have a separate cookie policy page, please provide the URL.
          </Typography>

          <InputLabel shrink sx={{ mb: 1 }}>
            Cookie Policy URL
          </InputLabel>

          <TextField
            fullWidth
            id="cookie-policy-url"
            name="config.cookies_tracking.cookie_policy_url"
            value={formData.config.cookies_tracking.cookie_policy_url}
            onChange={handleInputChange}
            placeholder="https://example.com/cookies"
            autoComplete="off"
            size="small"
            error={!!errors["config.cookies_tracking.cookie_policy_url"]}
            helperText={errors["config.cookies_tracking.cookie_policy_url"]}
          />
        </Box>


        <Divider sx={{ mt: 3, mb: 3 }} />

        <Box sx={{ mt: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="textSecondary">
            Children's Privacy
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Does your website specifically serve children?
          </Typography>

          <FormControl>
            <RadioGroup
              row
              aria-labelledby="childrens-privacy-label"
              value={formData.config.childrens_privacy.yes}
              onChange={(e) =>
                handleBooleanChange(
                  "config.childrens_privacy.yes",
                  e.target.value === "true"
                )
              }
            >
              <FormControlLabel
                value={true}
                control={<Radio id="children-yes" />}
                label="Yes"
              />
              <FormControlLabel
                value={false}
                control={<Radio id="children-no" />}
                label="No"
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <Divider sx={{ mt: 3, mb: 3 }} />

        <Box sx={{ mt: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="textSecondary">
            Marketing & Communication
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Information about marketing practices.
          </Typography>

          <FormControl>
            <FormLabel id="direct-marketing-label">Direct Marketing</FormLabel>
            <RadioGroup
              row
              aria-labelledby="direct-marketing-label"
              value={
                formData.config.marketing_and_communication.direct_marketing
              }
              onChange={(e) =>
                handleBooleanChange(
                  "config.marketing_and_communication.direct_marketing",
                  e.target.value === "true"
                )
              }
            >
              <FormControlLabel
                value={true}
                control={<Radio id="marketing-yes" />}
                label="Yes"
              />
              <FormControlLabel
                value={false}
                control={<Radio id="marketing-no" />}
                label="No"
              />
            </RadioGroup>
          </FormControl>

          {formData.config.marketing_and_communication.direct_marketing && (
            <FormGroup row sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Marketing Channels
              </Typography>
              <FormControl
                fullWidth
                error={
                  !!errors?.[
                  "config.marketing_and_communication.marketing_channels"
                  ]
                }
              >
                <Box sx={{ display: "flex", flexWrap: "wrap", width: "100%" }}>
                  {[
                    "email marketing",
                    "social media marketing",
                    "sms marketing",
                    "phone calls",
                    "direct mail",
                    "in-app messaging",
                  ].map((channel, index) => (
                    <FormControlLabel
                      key={channel}
                      control={
                        <Checkbox
                          id={`marketing-channel-${index}`}
                          checked={formData.config.marketing_and_communication.marketing_channels.includes(
                            channel
                          )}
                          onChange={(e) =>
                            handleArrayInputChange(
                              "config.marketing_and_communication.marketing_channels",
                              channel,
                              e.target.checked
                            )
                          }
                        />
                      }
                      label={channel}
                      sx={{ width: "33%", mb: 1 }}
                    />
                  ))}
                </Box>

                {errors?.[
                  "config.marketing_and_communication.marketing_channels"
                ] && (
                    <FormHelperText>
                      {
                        errors[
                        "config.marketing_and_communication.marketing_channels"
                        ]
                      }
                    </FormHelperText>
                  )}
              </FormControl>
            </FormGroup>
          )}
        </Box>

        <Divider sx={{ mt: 3, mb: 3 }} />

        <Box sx={{ mt: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="textSecondary">
            Breach Notification{" "}
            <Typography component="span" color="error">
              *
            </Typography>
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            How will you notify users in case of a data breach?
          </Typography>

          <FormGroup row>
            {["email notifications", "website notification"].map(
              (channel, index) => (
                <FormControlLabel
                  key={channel}
                  control={
                    <Checkbox
                      id={`breach-channel-${index}`}
                      checked={formData.config.breach_notification.breach_notification_channels.includes(
                        channel
                      )}
                      onChange={(e) =>
                        handleArrayInputChange(
                          "config.breach_notification.breach_notification_channels",
                          channel,
                          e.target.checked
                        )
                      }
                    />
                  }
                  label={channel}
                  sx={{ width: "33%", mb: 1 }}
                />
              )
            )}
          </FormGroup>
          {!!errors?.[
            "config.breach_notification.breach_notification_channels"
          ] && (
              <FormHelperText error>
                {
                  errors[
                  "config.breach_notification.breach_notification_channels"
                  ]
                }
              </FormHelperText>
            )}
        </Box>

        <Divider sx={{ mt: 3, mb: 3 }} />

        <Box sx={{ mt: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="textSecondary">
            User Rights
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Response time for user data requests.
          </Typography>
          <InputLabel shrink required>
            Response Days
          </InputLabel>
          <Slider
            value={formData.config.user_rights.response_days || 1}
            onChange={(e, value) => {
              setFormData((prev) => ({
                ...prev,
                config: {
                  ...prev.config,
                  user_rights: {
                    ...prev.config.user_rights,
                    response_days: value,
                  },
                },
              }));
            }}
            min={1}
            max={60}
            step={1}
            valueLabelDisplay="on"
          />
          {!!errors?.["config.user_rights.response_days"] && (
            <FormHelperText error>
              {errors["config.user_rights.response_days"]}
            </FormHelperText>
          )}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
          <ButtonCommon sx={{ borderColor: "lightgray" }} onClick={onBack}>
            Back
          </ButtonCommon>
          <Button
            type="submit"
            sx={{
              border: `2px solid #00B894`,
              borderRadius: "8px",
              padding: "8px 12px",
              textTransform: "capitalize",
              fontFamily: "DM Sans",
              fontSize: "15px",
              "&:hover": {
                backgroundColor: "#00B894",
                color: "#fff",
              },
              color: "#525455",
              fontWeight: "bold",
              "& > *:first-child": {
                marginRight: "8px",
              },
              height: "48px",
            }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : "Submit"}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default PolicySettings;
